import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';
import { Truck, Shield, ArrowLeft, ShoppingCart, Loader2, AlertTriangle } from 'lucide-react';

const ProductDetailPage = () => {
  const { handle } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const { addItem, cart } = useCart();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${handle}`).then((r) => setP(r.data)).catch(() => setP(null)).finally(() => setLoading(false));
  }, [handle]);

  if (loading) return <div className="max-w-6xl mx-auto px-4 mt-20 text-white/70"><Loader2 className="w-6 h-6 animate-spin inline" /> Cargando...</div>;
  if (!p) return (
    <div className="max-w-6xl mx-auto px-4 mt-20 text-center pb-32">
      <h2 className="text-2xl font-bold text-white">Producto no encontrado</h2>
      <Link to="/catalogo" className="mt-4 inline-block text-orange-400">Volver al catálogo</Link>
    </div>
  );

  const onSale = p.compare_at_price && p.compare_at_price > p.price;
  const images = p.images && p.images.length ? p.images : [p.main_image].filter(Boolean);

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-6 mt-6 pb-32">
      <Link to="/catalogo" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm">
        <ArrowLeft className="w-4 h-4" /> Volver al catálogo
      </Link>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="aspect-square bg-neutral-900 rounded-3xl overflow-hidden border border-white/10">
            {images[activeImg] && (
              <img src={images[activeImg]} alt={p.title} className="w-full h-full object-cover" />
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto scrollbar-hide">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-20 rounded-xl overflow-hidden shrink-0 border-2 ${i === activeImg ? 'border-orange-500' : 'border-white/10'}`}>
                  <img src={img} alt={`vista ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-orange-400 font-bold text-xs uppercase tracking-widest">{p.vendor}</p>
          <h1 className="mt-1 text-3xl md:text-4xl font-black text-white uppercase">{p.title}</h1>
          <div className="mt-3 flex items-baseline gap-3">
            <span className="text-4xl font-black text-white">${p.price?.toLocaleString('es-MX')}</span>
            {onSale && <span className="text-xl text-white/40 line-through">${p.compare_at_price?.toLocaleString('es-MX')}</span>}
          </div>
          <p className="mt-2 text-sm text-white/50">{p.category} · {p.currency || 'MXN'}</p>

          {Array.isArray(p.bulk_tiers) && p.bulk_tiers.length > 0 && (
            <div className="mt-4 rounded-2xl border border-orange-500/30 bg-gradient-to-br from-orange-500/10 via-orange-500/5 to-transparent p-4" data-testid="product-bulk-tiers-detail">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] font-black uppercase tracking-wider text-orange-400">Promo por cantidad</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {[...p.bulk_tiers].sort((a, b) => a.qty - b.qty).map((t) => {
                  const unit = t.price / t.qty;
                  const save = Math.max(0, (p.price * t.qty) - t.price);
                  return (
                    <div key={t.qty} className="flex-1 min-w-[140px] rounded-xl border border-orange-500/25 bg-black/40 px-3 py-2">
                      <div className="text-lg font-black text-white">
                        {t.qty}x <span className="text-orange-400">${Number(t.price).toLocaleString('es-MX', { minimumFractionDigits: 0 })}</span>
                      </div>
                      <div className="text-[11px] text-white/60">${unit.toLocaleString('es-MX', { maximumFractionDigits: 0 })} c/u{save > 0 ? ` · ahorra $${save.toLocaleString('es-MX', { maximumFractionDigits: 0 })}` : ''}</div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-white/40 mt-2">Se aplica automáticamente al carrito.</p>
            </div>
          )}

          {p.unavailable && (
            <div data-testid="unavailable-banner" className="mt-5 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-[15px]">Este producto no está disponible por ahora</p>
                  <p className="text-white/60 text-sm mt-1">
                    La categoría <span className="text-amber-300 font-semibold">{p.category}</span> se encuentra temporalmente oculta. Vuelve pronto o descubre otros productos increíbles en el catálogo.
                  </p>
                  <Link
                    to="/catalogo"
                    data-testid="unavailable-catalog-link"
                    className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-orange-500 hover:bg-orange-400 text-black text-xs font-black uppercase tracking-wider transition"
                  >
                    Explorar catálogo <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex gap-3">
            {(() => {
              const inCart = cart.items.find((i) => i.product_id === p.product_id);
              const cartQty = inCart?.qty || 0;
              const atStockLimit = p.stock != null && cartQty >= p.stock;
              return (
                <button
                  onClick={async () => { await addItem(p, 1); toast({ title: 'Agregado al carrito', description: p.title }); }}
                  disabled={!p.available || p.unavailable || atStockLimit}
                  data-testid="add-to-cart-btn"
                  className="flex-1 h-12 rounded-full bg-orange-500 text-black hover:bg-orange-600 font-bold flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {p.unavailable ? 'No disponible' : !p.available ? 'Agotado' : atStockLimit ? 'Límite alcanzado' : 'Agregar al carrito'}
                </button>
              );
            })()}
            <Link to="/carrito">
              <button className="h-12 px-6 rounded-full bg-white/10 text-white hover:bg-white/15 font-semibold border border-white/10">Ver carrito</button>
            </Link>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 bg-neutral-950 border border-white/10 rounded-xl p-3">
              <Truck className="w-5 h-5 text-orange-500" />
              <div>
                <p className="text-white text-sm font-semibold">Envío rápido</p>
                <p className="text-white/50 text-xs">Gratis +$1,350</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-neutral-950 border border-white/10 rounded-xl p-3">
              <Shield className="w-5 h-5 text-orange-500" />
              <div>
                <p className="text-white text-sm font-semibold">Garantía</p>
                <p className="text-white/50 text-xs">Contra defectos</p>
              </div>
            </div>
          </div>

          {p.description && (
            <div className="mt-8">
              <h3 className="font-bold text-white mb-2">Descripción</h3>
              <p className="text-white/70 leading-relaxed text-sm whitespace-pre-line">{p.description}</p>
            </div>
          )}

          {p.tags?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {p.tags.slice(0, 8).map((t) => (
                <span key={t} className="text-xs bg-white/5 border border-white/10 text-white/70 px-2.5 py-1 rounded-full">{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
